import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Building2, ArrowLeft, Globe, Linkedin, Users, MapPin, ExternalLink, GitBranch, Clock, Sparkles,
} from "lucide-react";
import { UnifiedContactModal } from "@/components/contacts/UnifiedContactModal";
import { LinkedInFollowUpPanel } from "@/components/contacts/panels/LinkedInFollowUpPanel";
import type { CampaignOutreachContact } from "@/types/outreach";
import { openExternal } from "@/lib/utils";

interface AccountDetailHeaderProps {
  company: Record<string, any>;
}

export function AccountDetailHeader({ company }: AccountDetailHeaderProps) {
  const navigate = useNavigate();
  const [draftOutreachOpen, setDraftOutreachOpen] = useState(false);
  const initials = company.company_name?.split(" ").slice(0, 2).map((w: string) => w[0]).join("").toUpperCase() || "?";
  const location = [company.headquarters_city, company.headquarters_state, company.headquarters_country].filter(Boolean).join(", ");

  // Fetch a top contact for this account so the Draft Outreach button has
  // someone to draft to. Pulls the first ICP-relevant outreach target.
  const { data: topContact } = useQuery({
    queryKey: ["account-top-contact", company.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("contacts")
        .select("id, first_name, last_name, full_name, job_title, company_id, email, linkedin_url, headline, seniority_level, department, location, marketing_persona, is_outreach_target")
        .eq("company_id", company.id)
        .eq("is_outreach_target", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!data) return null;
      const c = data as Record<string, any>;
      const draftContact: CampaignOutreachContact = {
        contact_id: c.id,
        first_name: c.first_name,
        last_name: c.last_name,
        full_name: c.full_name,
        job_title: c.job_title,
        company_name: company.company_name,
        company_id: company.id,
        account_domain: company.domain || null,
        email: c.email,
        linkedin_url: c.linkedin_url,
        headline: c.headline,
        seniority_level: c.seniority_level,
        department: c.department,
        location: c.location,
      };
      return draftContact;
    },
  });

  // Fetch parent info (if child) or child count (if parent)
  const { data: familyInfo } = useQuery({
    queryKey: ["company-family-header", company.id],
    queryFn: async () => {
      let parentId: string | null = null;
      let parentName: string | null = null;
      let childCount = 0;

      if (company.parent_company_id) {
        const { data: parent } = await supabase
          .from("companies")
          .select("id, company_name")
          .eq("id", company.parent_company_id)
          .single();
        const p = parent as { id: string; company_name: string } | null;
        parentId = p?.id ?? null;
        parentName = p?.company_name ?? null;
      }

      const { count } = await supabase
        .from("companies")
        .select("id", { count: "exact", head: true })
        .eq("parent_company_id", company.id);
      childCount = count ?? 0;

      return { parentId, parentName, childCount };
    },
  });

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2 -ml-2">
        <ArrowLeft className="h-4 w-4" />
        Back to Accounts
      </Button>
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="h-16 w-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xl font-semibold shrink-0">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.company_name} className="h-16 w-16 rounded-lg object-cover" />
            ) : (
              initials || <Building2 className="h-6 w-6" />
            )}
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold">{company.company_name}</h1>
            <div className="flex items-center gap-4 text-muted-foreground text-sm">
              {company.employee_range && (
                <span className="flex items-center gap-1"><Users className="h-4 w-4" />{company.employee_range}</span>
              )}
              {location && (
                <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{location}</span>
              )}
            </div>
            {company.last_reviewed_by && (
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  Last reviewed by {company.last_reviewed_by}
                  {company.last_reviewed_at && (
                    <> · {new Date(company.last_reviewed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</>
                  )}
                </span>
              </div>
            )}
            {/* Parent/Child Relationship */}
            {familyInfo?.parentName && familyInfo.parentId && (
              <div className="flex items-center gap-1.5 text-sm">
                <GitBranch className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">Subsidiary of</span>
                <Link
                  to={`/accounts/${familyInfo.parentId}`}
                  className="font-medium text-primary hover:underline"
                >
                  {familyInfo.parentName}
                </Link>
              </div>
            )}
            {familyInfo && familyInfo.childCount > 0 && (
              <div className="flex items-center gap-1.5 text-sm">
                <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-muted-foreground">
                  Parent company &mdash; {familyInfo.childCount} {familyInfo.childCount === 1 ? "subsidiary" : "subsidiaries"}
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {company.domain && (
            <Button variant="outline" size="sm" asChild>
              <a href={`https://${company.domain}`} target="_blank" rel="noopener noreferrer" className="gap-2">
                <Globe className="h-4 w-4" />Website<ExternalLink className="h-3 w-3" />
              </a>
            </Button>
          )}
          {company.linkedin_url && (
            <Button variant="outline" size="sm" className="gap-2" onClick={() => openExternal(company.linkedin_url)}>
              <Linkedin className="h-4 w-4" />LinkedIn
            </Button>
          )}
          {topContact && (
            <Button
              variant="default"
              size="sm"
              className="gap-2"
              onClick={() => setDraftOutreachOpen(true)}
              title={`Draft outreach to ${topContact.full_name || topContact.first_name || 'top contact'}`}
            >
              <Sparkles className="h-4 w-4" />Draft Outreach
            </Button>
          )}
        </div>
      </div>
      <UnifiedContactModal
        contactId={topContact?.contact_id ?? null}
        open={draftOutreachOpen}
        onOpenChange={setDraftOutreachOpen}
        mode="linkedin"
        panel={topContact?.contact_id ? <LinkedInFollowUpPanel contactId={topContact.contact_id} /> : undefined}
      />
    </div>
  );
}
